import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFileCsv,
  FaUserShield,
  FaUserMinus,
  FaExternalLinkAlt,
  FaCopy,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBriefcase,
  FaGraduationCap,
  FaLanguage,
  FaGift,
  FaInbox,
  FaTh,
  FaList,
  FaUserPlus,
  FaExclamationTriangle,
  FaMars,
  FaVenus,
  FaCalendarAlt,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes alFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes alFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes alSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes alZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.al-in { animation: alFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.al-fade { animation: alFade .2s ease both; }
.al-slide { animation: alSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.al-zoom { animation: alZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.al-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.al-press:hover { transform: translateY(-1px); }
.al-press:active { transform: scale(.96); }
.al-card { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.al-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -16px rgba(15,23,42,.35); }
.al-row { transition: background-color .15s ease; cursor: pointer; }
.al-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .al-in, .al-fade, .al-slide, .al-zoom { animation: none; }
  .al-press:hover, .al-press:active, .al-card:hover { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const joinedAt = (u) => (u?.reg_date ? parseAny(`${u.reg_date} ${u.reg_time || "00:00:00"}`) : null);
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const phoneOf = (u) => u?.phone_num || u?.contact || "";
const place = (u) => [u?.village, u?.district, u?.state].filter((x) => x && String(x).trim()).join(", ");

const waLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
};

const toArray = (d) => {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.admins)) return d.admins;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && (d.id || d.user_id)) return [d];
  return [];
};

const SEARCH_FIELDS = [
  { id: "all", label: "Search in: all fields" },
  { id: "name", label: "Name" },
  { id: "user_id", label: "User ID" },
  { id: "email_id", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "place", label: "Location" },
  { id: "reffral_code", label: "Referral code" },
];

const fieldValue = (u, f) => {
  if (f === "phone") return phoneOf(u);
  if (f === "place") return [place(u), u.address, u.country].join(" ");
  return u?.[f];
};

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="al-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ src, name, size = 40, ring = "" }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return (
      <img
        src={src}
        alt=""
        onError={() => setErr(true)}
        className={`rounded-full object-cover shrink-0 bg-slate-100 ${ring}`}
        style={{ width: size, height: size }}
      />
    );
  return (
    <span
      className={`rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200 ${ring}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
};

const StatusPill = ({ value, label }) => {
  const v = String(value || "").toLowerCase();
  const good = v === "live" || v === "active";
  const bad = v === "block" || v === "blocked";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${
        good
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
          : bad
          ? "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
          : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${good ? "bg-emerald-500" : bad ? "bg-rose-500" : "bg-slate-400"}`} />
      {label}: {good ? "Active" : bad ? "Blocked" : value || "-"}
    </span>
  );
};

/* ================= page ================= */

const AdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [searchIn, setSearchIn] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name:asc");
  const [view, setView] = useState("cards");
  const [selectedIds, setSelectedIds] = useState([]);

  const [drawer, setDrawer] = useState(null);
  const [confirm, setConfirm] = useState(null); // array of admins
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions as before) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const formData = new FormData();
      formData.append("action", action);
      Object.entries(extra).forEach(([k, v]) => formData.append(k, v));
      const res = await fetch(`${baseUrl}/users.php`, { method: "POST", body: formData, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const data = await post("all-admin");
      setAdmins(toArray(data));
      if (data && data.status === false && data.error) {
        toast.current?.show({ severity: "error", summary: "Error", detail: data.error });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch admins" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const runRemove = async () => {
    if (!confirm?.length) return;
    setWorking(true);
    let ok = 0;
    for (const a of confirm) {
      try {
        const data = await post("remove-admin", { user_id: a.user_id });
        if (data && data.status === false) throw new Error(data.error || "Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Removed",
        detail: ok === 1 ? "Admin removed successfully" : `${ok} admins removed successfully`,
      });
    }
    if (ok < confirm.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: `${confirm.length - ok} could not be removed` });
    }
    setConfirm(null);
    setSelectedIds([]);
    setDrawer(null);
    fetchAdmins();
  };

  /* ----- derived ----- */

  const genders = useMemo(() => Array.from(new Set(admins.map((a) => a.gender).filter(Boolean))), [admins]);
  const states = useMemo(
    () => Array.from(new Set(admins.map((a) => a.state).filter((s) => s && String(s).trim()))).sort(),
    [admins]
  );

  const q = query.trim();

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let list = admins.filter((a) => {
      if (genderFilter !== "all" && a.gender !== genderFilter) return false;
      if (stateFilter !== "all" && a.state !== stateFilter) return false;
      if (!ql) return true;
      const fields =
        searchIn === "all"
          ? ["name", "user_id", "email_id", "phone", "place", "reffral_code", "profession"]
          : [searchIn];
      return fields.some((f) => String(fieldValue(a, f) ?? "").toLowerCase().includes(ql));
    });
    const [field, dir] = sortBy.split(":");
    const m = dir === "asc" ? 1 : -1;
    list = [...list].sort((x, y) => {
      let a;
      let b;
      if (field === "joined") {
        a = joinedAt(x)?.getTime() || 0;
        b = joinedAt(y)?.getTime() || 0;
      } else {
        a = String(x[field] || "").toLowerCase();
        b = String(y[field] || "").toLowerCase();
      }
      if (a < b) return -1 * m;
      if (a > b) return 1 * m;
      return 0;
    });
    return list;
  }, [admins, q, searchIn, genderFilter, stateFilter, sortBy]);

  useEffect(() => {
    const valid = new Set(admins.map((a) => a.user_id));
    setSelectedIds((ids) => ids.filter((id) => valid.has(id)));
  }, [admins]);

  const stats = useMemo(
    () => ({
      total: admins.length,
      active: admins.filter((a) => String(a.status).toLowerCase() === "live").length,
      states: new Set(admins.map((a) => a.state).filter(Boolean)).size,
      male: admins.filter((a) => String(a.gender).toLowerCase() === "male").length,
      female: admins.filter((a) => String(a.gender).toLowerCase() === "female").length,
    }),
    [admins]
  );

  const selectedAdmins = admins.filter((a) => selectedIds.includes(a.user_id));
  const allSelected = filtered.length > 0 && filtered.every((a) => selectedIds.includes(a.user_id));
  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleAll = () => setSelectedIds(allSelected ? [] : filtered.map((a) => a.user_id));

  const hasFilters = q || genderFilter !== "all" || stateFilter !== "all";
  const clearFilters = () => {
    setQuery("");
    setGenderFilter("all");
    setStateFilter("all");
  };
  const hq = (f) => (searchIn === "all" || searchIn === f ? q : "");

  /* ----- misc ----- */

  const copyText = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = (list) => {
    const head = ["User ID", "Name", "Email", "Phone", "Gender", "DOB", "Location", "Profession", "Study", "Language", "Referral Code", "Admin Status", "Account Status", "Reg. Date", "Reg. Time"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = list.map((a) =>
      [a.user_id, a.name, a.email_id, phoneOf(a), a.gender, a.dob, place(a), a.profession, a.study, a.language, a.reffral_code, a.status, a.account_status, a.reg_date, a.reg_time]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ycnp-admins-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (e.key === "/" && !typing && !confirm && !drawer) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key !== "Escape") return;
      if (confirm && !working) setConfirm(null);
      else if (drawer) setDrawer(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, drawer]);

  /* ----- UI bits ----- */

  const statCards = [
    { label: "Total admins", value: stats.total, icon: FaUserShield, tint: "bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300" },
    { label: "Active admins", value: stats.active, icon: FaUserShield, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
    { label: "States covered", value: stats.states, icon: FaMapMarkerAlt, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" },
    { label: "Male / Female", value: `${stats.male} / ${stats.female}`, icon: FaMars, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300" },
  ];

  const Actions = ({ a, full }) => (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => navigate(`/user-profile/${a.user_id}`)}
        className={`al-press h-9 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 inline-flex items-center justify-center gap-2 ${full ? "flex-1" : "w-9"}`}
        title="Open full profile"
        aria-label="Open full profile"
      >
        <FaExternalLinkAlt size={11} /> {full && "Profile"}
      </button>
      <button
        type="button"
        onClick={() => setConfirm([a])}
        className={`al-press h-9 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white dark:bg-rose-900/30 dark:text-rose-300 text-sm font-medium inline-flex items-center justify-center gap-2 ${full ? "flex-1" : "px-3"}`}
      >
        <FaUserMinus size={12} /> Remove
      </button>
    </div>
  );

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="al-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">App Users</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Admin List</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">People with admin manager rights in the app.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin-req")}
            className="al-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50"
          >
            <FaUserPlus size={12} /> Admin requests
          </button>
          <button
            type="button"
            onClick={() => exportCsv(filtered)}
            disabled={!filtered.length}
            className="al-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <FaFileCsv /> Export
          </button>
          <button
            type="button"
            onClick={fetchAdmins}
            disabled={loading}
            className="al-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} al-in p-4 flex items-center gap-4`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                {loading && !admins.length ? (
                  <div className="h-6 w-12 mt-1 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                    {typeof c.value === "number" ? fmt(c.value) : c.value}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className={`${CARD} al-in mt-4 p-4`} style={{ animationDelay: "240ms" }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search admins by name, ID, email, phone, location or referral code"
              className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {query ? (
                <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                  <FaTimes size={13} />
                </button>
              ) : (
                <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
              )}
            </div>
          </div>
          <select value={searchIn} onChange={(e) => setSearchIn(e.target.value)} className={`${INPUT} h-12 px-3 md:w-52`} aria-label="Search in">
            {SEARCH_FIELDS.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} className={`${INPUT} h-9 px-3`}>
            <option value="all">All genders</option>
            {genders.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className={`${INPUT} h-9 px-3`}>
            <option value="all">All states</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-9 px-3`}>
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
            <option value="joined:desc">Newest joined</option>
            <option value="joined:asc">Oldest joined</option>
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
              Clear all
            </button>
          )}
          <div className="ml-auto inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
            {[
              { id: "cards", icon: FaTh, label: "Card view" },
              { id: "table", icon: FaList, label: "Table view" },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setView(v.id)}
                  aria-label={v.label}
                  title={v.label}
                  className={`w-8 h-7 rounded-md flex items-center justify-center transition-all active:scale-95 ${
                    view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"
                  }`}
                >
                  <Icon size={12} />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Results bar */}
      <div className="flex flex-wrap items-center gap-3 mt-4 mb-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-cyan-600" aria-label="Select all results" />
          Select all
        </label>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {q ? (
            <>
              <b>{filtered.length}</b> result{filtered.length === 1 ? "" : "s"} for "<b>{q}</b>"
            </>
          ) : (
            <>
              <b>{filtered.length}</b> of {admins.length} admins
            </>
          )}
        </p>
        {selectedIds.length > 0 && (
          <div className="al-fade ml-auto flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{selectedIds.length} selected</span>
            <button
              type="button"
              onClick={() => setConfirm(selectedAdmins)}
              className="al-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium"
            >
              <FaUserMinus size={11} /> Remove selected
            </button>
            <button type="button" onClick={() => exportCsv(selectedAdmins)} className="al-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
              <FaFileCsv size={11} /> Export
            </button>
            <button type="button" onClick={() => setSelectedIds([])} className="text-sm text-slate-500 hover:underline">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {loading && !admins.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${CARD} h-64 animate-pulse`} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className={`${CARD} px-5 py-14 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={34} />
          <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">
            {q ? `No admin found for "${q}"` : admins.length ? "No admins match these filters" : "No admins yet"}
          </p>
          {hasFilters ? (
            <button type="button" onClick={clearFilters} className="mt-2 text-sm text-cyan-700 font-medium hover:underline">
              Clear all filters
            </button>
          ) : (
            <button type="button" onClick={() => navigate("/admin-req")} className="al-press mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 text-white text-sm font-semibold">
              <FaUserPlus size={12} /> See admin requests
            </button>
          )}
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((a, i) => {
            const sel = selectedIds.includes(a.user_id);
            const loc = place(a);
            return (
              <div
                key={a.user_id || i}
                onClick={() => setDrawer(a)}
                className={`al-card al-in relative rounded-xl border bg-white dark:bg-secondary-dark-bg overflow-hidden cursor-pointer ${
                  sel ? "border-cyan-500 ring-2 ring-cyan-500/30" : "border-slate-200 dark:border-slate-700"
                }`}
                style={{ animationDelay: `${Math.min(i, 12) * 50}ms` }}
              >
                <div className="h-16 bg-gradient-to-r from-indigo-500 via-cyan-500 to-emerald-400" />
                <input
                  type="checkbox"
                  checked={sel}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(a.user_id)}
                  className="absolute top-3 left-3 w-4 h-4 accent-cyan-600 cursor-pointer"
                  aria-label={`Select ${a.name}`}
                />
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-white/90 text-indigo-700">
                  <FaUserShield size={10} /> Admin
                </span>
                <div className="px-5 pb-5 -mt-9">
                  <Avatar src={a.profile_img} name={a.name} size={72} ring="ring-4 ring-white dark:ring-slate-800" />
                  <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-white truncate">
                    <Hi text={a.name || "No name"} q={hq("name")} />
                  </h3>
                  <p className="text-xs text-slate-500 tabular-nums">
                    ID <Hi text={a.user_id} q={hq("user_id")} />
                  </p>

                  <ul className="mt-3 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                    <li className="flex items-center gap-2 min-w-0">
                      <FaEnvelope className="text-slate-400 shrink-0" size={12} />
                      <span className="truncate"><Hi text={a.email_id || "-"} q={hq("email_id")} /></span>
                    </li>
                    <li className="flex items-center gap-2">
                      <FaPhoneAlt className="text-slate-400 shrink-0" size={11} />
                      <span className="tabular-nums"><Hi text={phoneOf(a) || "-"} q={hq("phone")} /></span>
                    </li>
                    <li className="flex items-center gap-2 min-w-0">
                      <FaMapMarkerAlt className="text-slate-400 shrink-0" size={12} />
                      <span className="truncate"><Hi text={loc || "Location not added"} q={hq("place")} /></span>
                    </li>
                  </ul>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <StatusPill value={a.status} label="Admin" />
                    {a.account_status && <StatusPill value={a.account_status} label="Account" />}
                  </div>

                  <div className="mt-4">
                    <Actions a={a} full />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="pl-4 py-3 w-8" />
                  <th className="px-3 py-3 font-medium text-left">Admin</th>
                  <th className="px-3 py-3 font-medium text-left">Email</th>
                  <th className="px-3 py-3 font-medium text-left">Phone</th>
                  <th className="px-3 py-3 font-medium text-left">Location</th>
                  <th className="px-3 py-3 font-medium text-left">Role</th>
                  <th className="px-3 py-3 font-medium text-left whitespace-nowrap">Reg. Date</th>
                  <th className="px-3 py-3 pr-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filtered.map((a, i) => {
                  const d = joinedAt(a);
                  return (
                    <tr key={a.user_id || i} onClick={() => setDrawer(a)} className="al-row hover:bg-slate-50 dark:hover:bg-slate-700/40">
                      <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.includes(a.user_id)} onChange={() => toggleSelect(a.user_id)} className="w-4 h-4 accent-cyan-600 cursor-pointer" aria-label={`Select ${a.name}`} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3 min-w-[180px]">
                          <Avatar src={a.profile_img} name={a.name} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate"><Hi text={a.name || "No name"} q={hq("name")} /></p>
                            <p className="text-xs text-slate-500 tabular-nums"><Hi text={a.user_id} q={hq("user_id")} /></p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-700 dark:text-slate-200"><Hi text={a.email_id || "-"} q={hq("email_id")} /></td>
                      <td className="px-3 py-3 whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-200"><Hi text={phoneOf(a) || "-"} q={hq("phone")} /></td>
                      <td className="px-3 py-3 text-slate-700 dark:text-slate-200"><Hi text={place(a) || "-"} q={hq("place")} /></td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                          <FaUserShield size={10} /> Admin
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                        <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                      </td>
                      <td className="px-3 py-3 pr-4">
                        <div className="flex justify-end">
                          <Actions a={a} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= Drawer ================= */}
      {drawer && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="al-fade absolute inset-0 bg-slate-900/40" onClick={() => setDrawer(null)} />
          <aside className="al-slide relative w-full max-w-md h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="relative">
              <div className="h-24 bg-gradient-to-r from-indigo-500 via-cyan-500 to-emerald-400" />
              <button
                type="button"
                onClick={() => setDrawer(null)}
                className="al-press absolute top-3 right-3 w-9 h-9 rounded-lg bg-white/90 flex items-center justify-center text-slate-700"
                aria-label="Close"
              >
                <FaTimes />
              </button>
              <div className="px-5 -mt-10">
                <Avatar src={drawer.profile_img} name={drawer.name} size={84} ring="ring-4 ring-white dark:ring-slate-800" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-5">
              <h2 className="mt-2 text-xl font-semibold text-slate-900 dark:text-white">{drawer.name || "No name"}</h2>
              <button type="button" onClick={() => copyText(drawer.user_id, "User ID copied")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 tabular-nums">
                ID {drawer.user_id} <FaCopy size={10} />
              </button>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <StatusPill value={drawer.status} label="Admin" />
                {drawer.account_status && <StatusPill value={drawer.account_status} label="Account" />}
              </div>

              {/* Contact */}
              <section className="mt-5 rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                {[
                  { icon: FaEnvelope, label: "Email", value: drawer.email_id, href: drawer.email_id ? `mailto:${drawer.email_id}` : null },
                  { icon: FaPhoneAlt, label: "Phone", value: phoneOf(drawer), href: phoneOf(drawer) ? `tel:${phoneOf(drawer)}` : null },
                ].map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.label} className="flex items-center gap-3 px-4 py-3">
                      <Icon className="text-slate-400 shrink-0" size={13} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">{r.label}</p>
                        {r.href ? (
                          <a href={r.href} className="text-sm text-slate-900 dark:text-white hover:underline break-all">{r.value}</a>
                        ) : (
                          <p className="text-sm text-slate-400">-</p>
                        )}
                      </div>
                      {r.value && (
                        <button type="button" onClick={() => copyText(r.value)} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label={`Copy ${r.label}`}>
                          <FaCopy size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </section>

              {waLink(phoneOf(drawer)) && (
                <a href={waLink(phoneOf(drawer))} target="_blank" rel="noreferrer" className="al-press mt-3 flex items-center justify-center gap-2 h-10 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm font-semibold">
                  <FaWhatsapp size={16} /> Message on WhatsApp
                </a>
              )}

              {/* Details */}
              <section className="mt-5">
                <h3 className="text-xs font-semibold text-slate-500 mb-2">Details</h3>
                <dl className="grid grid-cols-2 gap-3">
                  {[
                    { icon: drawer.gender?.toLowerCase() === "female" ? FaVenus : FaMars, label: "Gender", value: drawer.gender },
                    { icon: FaCalendarAlt, label: "DOB", value: drawer.dob },
                    { icon: FaBriefcase, label: "Profession", value: drawer.profession },
                    { icon: FaGraduationCap, label: "Study", value: drawer.study },
                    { icon: FaLanguage, label: "Language", value: drawer.language },
                    { icon: FaCalendarAlt, label: "Joined", value: `${fmtDate(joinedAt(drawer))}` },
                  ].map((r) => {
                    const Icon = r.icon;
                    return (
                      <div key={r.label} className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                        <dt className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Icon size={11} /> {r.label}
                        </dt>
                        <dd className="text-sm font-medium text-slate-900 dark:text-white mt-0.5 break-words">{r.value || "-"}</dd>
                      </div>
                    );
                  })}
                </dl>
              </section>

              {/* Address */}
              <section className="mt-5">
                <h3 className="text-xs font-semibold text-slate-500 mb-2">Address</h3>
                <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3 text-sm text-slate-800 dark:text-slate-100 flex gap-2">
                  <FaMapMarkerAlt className="text-slate-400 mt-0.5 shrink-0" />
                  <span>
                    {[drawer.address, drawer.village, drawer.district, drawer.state, drawer.country].filter((x) => x && String(x).trim()).join(", ") || "Not added"}
                  </span>
                </div>
              </section>

              {/* Referral */}
              {(drawer.reffral_code || drawer.reffer_by) && (
                <section className="mt-5">
                  <h3 className="text-xs font-semibold text-slate-500 mb-2">Referral</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => copyText(drawer.reffral_code, "Referral code copied")}
                      className="al-press text-left rounded-lg border border-dashed border-cyan-300 dark:border-cyan-700 p-3"
                    >
                      <p className="flex items-center gap-1.5 text-xs text-slate-500"><FaGift size={11} /> Own code</p>
                      <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 tracking-wide">{drawer.reffral_code || "-"}</p>
                    </button>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
                      <p className="text-xs text-slate-500">Referred by</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white break-all">{drawer.reffer_by || "-"}</p>
                    </div>
                  </div>
                </section>
              )}
            </div>

            <div className="flex gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <button
                type="button"
                onClick={() => navigate(`/user-profile/${drawer.user_id}`)}
                className="al-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-semibold text-slate-800 dark:text-slate-100 inline-flex items-center justify-center gap-2"
              >
                <FaExternalLinkAlt size={11} /> Full profile
              </button>
              <button
                type="button"
                onClick={() => setConfirm([drawer])}
                className="al-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <FaUserMinus size={12} /> Remove admin
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="al-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="al-zoom relative w-full max-w-md rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-11 h-11 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 flex items-center justify-center">
              <FaExclamationTriangle size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              Remove {confirm.length === 1 ? confirm[0].name || "this admin" : `${confirm.length} admins`} from admin?
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              They lose admin manager rights. Their user account stays in the app. You can make them admin again from Admin Requests if they apply.
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-lg bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.map((a) => (
                <div key={a.user_id} className="flex items-center gap-3 p-2.5">
                  <Avatar src={a.profile_img} name={a.name} size={30} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{a.name || "No name"}</p>
                    <p className="text-xs text-slate-500 tabular-nums">{a.user_id}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="al-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button
                type="button"
                onClick={runRemove}
                disabled={working}
                className="al-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaUserMinus size={12} />}
                {working ? "Removing..." : "Yes, remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminList;